import { getAssetsByType } from '../core/assetsService.js';
import { getUserProfile, userService } from '../auth/userService.js';
import { featureFlagsService } from './featureFlagsService.js';
import { getTenantConfig } from './tenantService.js';
import { withRetry } from '../../utils/withRetry';

// Asset Type para Skin: 9
const THEME_ASSET_TYPE = 9;

// Cache para evitar múltiplas chamadas com os mesmos parâmetros
const themeCache = new Map();

// Promises em andamento para evitar múltiplas requisições simultâneas
const pendingRequests = new Map();

/**
 * Gera chave de cache baseada nos parâmetros
 */
const getCacheKey = (clientId, language, profile) => {
  return `${clientId}:${language}:${profile || 'default'}`;
};

/**
 * Verifica se uma string parece ser um UUID
 */
const isUUID = (str) => {
  if (!str || typeof str !== 'string') return false;
  // Formato UUID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Processa os assets do CMS para obter o tema
 * O campo 'url' do asset contém o JSON do tema
 * @param {Array} assets - Lista de assets retornados do CMS
 * @param {string|null} userSegmentId - UUID do segment do usuário (para filtrar por profile)
 * @param {string|null} targetAssetId - ID do asset (UUID) que estamos procurando (ex: SEGMENT_DEFAULT)
 * @returns {Promise<Object|null>} Dados do tema ou null
 */
const processThemeAsset = async (assets, userSegmentIds = [], targetAssetId = null) => {
  try {
    if (!assets || assets.length === 0) return null;

    const fetchUrl = async (asset) => {
      const response = await fetch(asset.url);
      return response.json();
    };

    // Prioridade 1: primeiro segmento do usuário (mais recente) que bater com algum asset
    for (const segmentId of userSegmentIds) {
      if (!isUUID(segmentId)) continue;
      const asset = assets.find(
        a => a.profiles && Array.isArray(a.profiles) && a.profiles.includes(segmentId)
      );
      if (asset?.url) return fetchUrl(asset);
    }

    // Prioridade 2: SEGMENT_DEFAULT (busca asset pelo profiles contendo o ID default)
    if (targetAssetId && isUUID(targetAssetId)) {
      const asset = assets.find(a => a.id === targetAssetId);
      if (asset?.url) return fetchUrl(asset);
    }

    // Prioridade 3: asset público (sem profiles)
    const pub = assets.find(
      a => !a.profiles || (Array.isArray(a.profiles) && a.profiles.length === 0)
    );
    if (pub?.url) return fetchUrl(pub);

    // Fallback: primeiro disponível
    if (assets[0]?.url) return fetchUrl(assets[0]);

    return null;
  } catch (error) {
    console.error('Error processing theme asset:', error);
    return null;
  }
};

export const themeService = {
  /**
   * Busca tema do CMS
   * @param {string} clientId - ID do cliente/tenant
   * @param {string} language - Idioma (padrão: 'pt-BR')
   * @param {string|null} profile - Perfil do usuário (null = auto-detecta)
   * @param {boolean} useCache - Se deve usar cache (padrão: true)
   * @returns {Promise<Object|null>} Objeto com tema ou null
   */
  async fetchTheme(clientId, language = 'pt-BR', profile = null, useCache = true) {
    try {
      // Se o profile não foi fornecido, determina qual usar
      let themeProfile = profile;
      
      // Variáveis para controlar a busca
      let userSegmentIds = [];
      let defaultAssetId = null;

      if (!themeProfile) {
        if (userService.isAuthenticated()) {
          try {
            const segments = await userService.getUserSegments();
            userSegmentIds = segments.map(s => s.segmentId).filter(isUUID);
          } catch (error) {
            console.error('[fetchTheme] Error fetching user segments, will use default asset:', error);
          }

          try {
            const tenantConfig = getTenantConfig();
            if (tenantConfig?.SEGMENT_DEFAULT && isUUID(tenantConfig.SEGMENT_DEFAULT)) {
              defaultAssetId = tenantConfig.SEGMENT_DEFAULT;
            } else {
              const featureFlags = await featureFlagsService.getFeatureFlags(language);
              if (featureFlags?.SEGMENT_DEFAULT && isUUID(featureFlags.SEGMENT_DEFAULT)) {
                defaultAssetId = featureFlags.SEGMENT_DEFAULT;
              }
            }
          } catch (error) {
            console.warn('Error fetching SEGMENT_DEFAULT for fallback:', error);
          }
        } else {
          try {
            const tenantConfig = getTenantConfig();
            if (tenantConfig?.SEGMENT_DEFAULT && isUUID(tenantConfig.SEGMENT_DEFAULT)) {
              defaultAssetId = tenantConfig.SEGMENT_DEFAULT;
            } else {
              const featureFlags = await featureFlagsService.getFeatureFlags(language);
              if (featureFlags?.SEGMENT_DEFAULT && isUUID(featureFlags.SEGMENT_DEFAULT)) {
                defaultAssetId = featureFlags.SEGMENT_DEFAULT;
              }
            }
          } catch (error) {
            console.warn('Error fetching SEGMENT_DEFAULT, using default profile:', error);
          }
        }
      } else if (isUUID(themeProfile)) {
        if (userService.isAuthenticated()) {
          userSegmentIds = [themeProfile];
        } else {
          defaultAssetId = themeProfile;
        }
      }

      // Chave de cache: primeiro segmento do usuário (mais prioritário) ou default
      const cacheKey = getCacheKey(clientId, language, userSegmentIds[0] || defaultAssetId || 'default');
      
      // Verifica cache se habilitado
      if (useCache && themeCache.has(cacheKey)) {
        return themeCache.get(cacheKey);
      }
      
      // Se já existe uma requisição em andamento para esta chave, retorna a mesma promise
      if (useCache && pendingRequests.has(cacheKey)) {
        return await pendingRequests.get(cacheKey);
      }
      
      // Cria uma nova promise para a requisição
      const requestPromise = (async () => {
        try {
      // Busca assets do CMS usando o novo serviço unificado, com retry:
      // uma falha transitória de rede aqui deixava o tema em branco até um refresh manual
          const themeData = await withRetry(async () => {
            // Nota: O endpoint retorna todos os assets do tipo, não filtra por profile no backend
            // O profile será usado apenas para identificar qual asset buscar (se for UUID)
            const assets = await getAssetsByType(THEME_ASSET_TYPE, {
              language,
              // Não passa profile aqui, pois o backend não filtra por profile
              // O profile será usado apenas para buscar o asset correto pelo ID
              requireAuth: false, // Theme pode ser público
            });

            // Processa o asset para obter o tema
            // Passa userSegmentId (prioridade) e defaultAssetId (fallback)
            const data = await processThemeAsset(assets, userSegmentIds, defaultAssetId);
            if (!data) throw new Error('Theme data not found');
            return data;
          });

          // Armazena no cache se habilitado
          if (useCache && themeData) {
            themeCache.set(cacheKey, themeData);
          }
      
      return themeData;
    } catch (error) {
      console.error('Error fetching theme:', error);
      return null;
        } finally {
          // Remove a promise pendente após completar (sucesso ou erro)
          pendingRequests.delete(cacheKey);
        }
      })();
      
      // Armazena a promise pendente
      if (useCache) {
        pendingRequests.set(cacheKey, requestPromise);
      }
      
      return await requestPromise;
    } catch (error) {
      console.error('Error fetching theme:', error);
      return null;
    }
  },
  
  /**
   * Limpa o cache de temas
   */
  clearCache() {
    themeCache.clear();
    pendingRequests.clear();
  },
  
  /**
   * Remove entrada específica do cache
   */
  clearCacheEntry(clientId, language, profile) {
    const cacheKey = getCacheKey(clientId, language, profile);
    themeCache.delete(cacheKey);
    pendingRequests.delete(cacheKey);
  },
  
  /**
   * Limpa cache para um clientId específico (útil quando muda autenticação)
   */
  clearCacheForClient(clientId) {
    const keysToDelete = [];
    for (const key of themeCache.keys()) {
      if (key.startsWith(`${clientId}:`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      themeCache.delete(key);
      pendingRequests.delete(key);
    });
  }
}; 
